/**
 * The storefronts a game can also be bought on.
 *
 * A game on the shelf here may also have a Steam page or an Epic one, and the game page says so
 * under "Also available on". Both are optional and most games have neither.
 *
 * The table is the single description of each storefront: its name, the hosts its pages live
 * on, and what one of its addresses looks like. The admin form reads it to label and check the
 * fields, the API reads it to check them again, and the game page reads it to draw the links,
 * so adding a third storefront is a row here, a column, and nothing else.
 */

import { z } from 'zod';

export const GAME_STORE_IDS = ['steam', 'epic'] as const;
export const gameStoreIdSchema = z.enum(GAME_STORE_IDS);
export type GameStoreId = z.infer<typeof gameStoreIdSchema>;

export interface GameStoreSpec {
  id: GameStoreId;
  /** The storefront's own name for itself, which is what the link says. */
  label: string;
  /**
   * Hosts a page for this storefront lives on, `www.` stripped before the comparison. More than
   * one because a storefront outlives its domain and old links keep working.
   */
  hosts: readonly string[];
  /**
   * What the path of a product page looks like. A host on its own is not enough: a link to a
   * storefront's front page under "Also available on" is a dead end for whoever clicks it.
   */
  path: RegExp;
  /** Shown under the field, so the shape is visible without having to fail a save first. */
  example: string;
}

export const GAME_STORES: readonly GameStoreSpec[] = [
  {
    id: 'steam',
    label: 'Steam',
    hosts: ['store.steampowered.com'],
    // An app, a bundle or a package, each numbered. Steam's own links carry a title after the
    // number and a query string after that; neither is required and neither is checked.
    path: /^\/(app|bundle|sub)\/\d+(\/|$)/,
    example: 'https://store.steampowered.com/app/1091500/Cyberpunk_2077/',
  },
  {
    id: 'epic',
    label: 'Epic Games Store',
    hosts: ['store.epicgames.com'],
    // Epic puts an optional locale in front of the path, so `/p/slug` and `/en-US/p/slug` are
    // the same page. `/bundles/` is the same page for something sold as a bundle.
    path: /^\/([a-z]{2}(-[A-Za-z]{2})?\/)?(p|bundles)\/[^/]+/,
    example: 'https://store.epicgames.com/en-US/p/your-game',
  },
];

export const STORE_BY_ID: Record<GameStoreId, GameStoreSpec> = Object.fromEntries(
  GAME_STORES.map((s) => [s.id, s]),
) as Record<GameStoreId, GameStoreSpec>;

/**
 * What is wrong with a store address, in the words the form shows.
 *
 * Null means it is fine, and so does an empty string: these fields are optional, and clearing
 * one is how a game that has left a storefront stops linking to it.
 *
 * The check is deliberately shallow. Whether the page behind the address exists is not
 * something a regular expression knows, and pretending otherwise would mean refusing a game on
 * the day it is announced. This only catches the two mistakes that actually happen: the wrong
 * storefront pasted into the wrong field, and a link to somewhere that is not a product page.
 */
export function storeUrlProblem(store: GameStoreId, raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  if (value === '') return null;

  const spec = STORE_BY_ID[store];

  // Every message below is phrased around the storefront's name rather than in front of it:
  // "a Epic Games Store address" is what an article costs, and the labels come from a table
  // that a third storefront will add to.
  const parts = splitUrl(value);
  if (!parts) return `${spec.label} addresses start with https://, like ${spec.example}`;

  if (!spec.hosts.includes(parts.host)) {
    return `${spec.label} pages are on ${spec.hosts[0]}. That address is somewhere else.`;
  }

  if (!spec.path.test(parts.path)) {
    return `That address is on ${spec.hosts[0]} but does not point at a game. It should look like ${spec.example}`;
  }

  return null;
}

/**
 * An absolute http(s) address, split into the two parts these checks read.
 *
 * Written by hand rather than with `URL` because this package compiles with neither the DOM
 * library nor the Workers types — it is the one piece of code both the API and the app share,
 * and keeping it free of a platform's globals is what stops something browser-only reaching the
 * Worker. Null for anything that is not absolute http(s), which is also what makes this an
 * allowlist: whatever comes back was spelled `http://` or `https://`.
 *
 * The `@` is the part worth being careful about. In `https://store.steampowered.com@evil.test/`
 * the host is `evil.test` and everything before the `@` is a username, so a check that read the
 * authority as written would follow a link to somewhere else entirely.
 */
function splitUrl(value: string): { host: string; path: string } | null {
  const match = /^https?:\/\/([^/?#\s]+)([^?#\s]*)/i.exec(value);
  if (!match) return null;

  const authority = match[1]!;
  // Whatever follows the last `@` is the host, then the port comes off the end of that.
  const hostAndPort = authority.slice(authority.lastIndexOf('@') + 1);
  const host = hostAndPort
    .replace(/:\d*$/, '')
    .toLowerCase()
    .replace(/^www\./, '');
  if (host === '') return null;

  return { host, path: match[2] || '/' };
}

/**
 * The app id out of a Steam address, when it is one.
 *
 * Worth pulling out because a description pasted from Steamworks refers to its own artwork with
 * `{STEAM_APP_IMAGE}`, a placeholder Steam expands to the app's folder on its CDN. Without the
 * id those images have no address and the pictures in an imported description are lost, so the
 * renderer expands the same token and this is where it gets the number from.
 *
 * Null for a bundle or a package link, which have no app of their own to expand against.
 */
export function steamAppId(url: string | null | undefined): string | null {
  if (storeUrlProblem('steam', url) !== null || !url) return null;
  const match = splitUrl(url.trim())?.path.match(/^\/app\/(\d+)/);
  return match ? match[1]! : null;
}

/**
 * Where Steam keeps an app's store artwork.
 *
 * The classic path, which is what `{STEAM_APP_IMAGE}` has always expanded to and what every
 * description written against it points into.
 */
export function steamAppImageBase(appId: string): string {
  return `https://cdn.akamai.steamstatic.com/steam/apps/${appId}`;
}

/**
 * A store address as it is stored: trimmed, and empty turned into null.
 *
 * A field someone cleared and a field nobody ever filled in are the same thing, and only one of
 * them should be able to reach the column — otherwise the game page has to test for both.
 */
export function normalizeStoreUrl(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  return value === '' ? null : value;
}

/**
 * A zod field for one storefront's address.
 *
 * `superRefine` rather than `refine` so the reason travels with the rejection: the form already
 * knows why an address is wrong, and a save it somehow let through should come back saying the
 * same thing rather than a bare "invalid".
 */
export function storeUrlSchema(store: GameStoreId) {
  return z
    .string()
    .max(500)
    .nullable()
    .optional()
    .transform(normalizeStoreUrl)
    .superRefine((value, ctx) => {
      const problem = storeUrlProblem(store, value);
      if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
    });
}
